import { Text } from "@solomon-labs/design/components/ui";
import type React from "react";

import LogoFull from "../../assets/logos/solomon-full.svg";

/** Inset fields on the raised auth panel (login, claim, etc.). */
export const AUTH_INPUT_CLASS =
    "h-11 bg-surface-ground border-line-base px-3.5 text-base leading-6";

/** Read-only display fields — sunken surface + imprint text signals non-editable. */
export const AUTH_READONLY_INPUT_CLASS =
    "h-11 bg-surface-sunken border-line-neutral px-3.5 text-base leading-6 text-foreground-imprint cursor-default hover:border-line-neutral focus-visible:border-line-neutral";

export function OrgAuthField({
    id,
    label,
    labelEnd,
    children
}: {
    id: string;
    label: string;
    labelEnd?: React.ReactNode;
    children: React.ReactNode;
}): React.ReactElement {
    return (
        // grid-cols-[1fr_auto]: label row layout that grid placement can target —
        // labelEnd sits after the input in the DOM (tab order: input first) but is
        // placed back onto the label row visually.
        <div className="grid grid-cols-[1fr_auto] items-baseline gap-y-2">
            <Text
                variant="label"
                className="text-foreground-muted"
                render={<label htmlFor={id} />}
            >
                {label}
            </Text>
            <div className="col-span-2 row-start-2 flex flex-col gap-2">
                {children}
            </div>
            {labelEnd !== null && labelEnd !== undefined && (
                <div className="col-start-2 row-start-1">{labelEnd}</div>
            )}
        </div>
    );
}

export function OrgAuthOrDivider(): React.ReactElement {
    return (
        <div className="flex items-center gap-3" aria-hidden="true">
            <div className="h-px flex-1 bg-line-base" />
            <Text variant="label" className="text-foreground-imprint">
                or
            </Text>
            <div className="h-px flex-1 bg-line-base" />
        </div>
    );
}

interface OrgAuthShellProps {
    /** Optional page title below the logo (claim, success states). */
    title?: string;
    /** Intro copy below the logo / title. */
    intro?: string;
    /** Centered fine print below the main content. */
    footerNote?: string;
    /**
     * Brand block at the top of the auth panel. Defaults to the Solomon
     * lockup — supplied only by co-branded consoles (e.g. the Western Union
     * POC) that sign in under a partner mark.
     */
    logo?: React.ReactNode;
    children: React.ReactNode;
}

export function OrgAuthShell({
    title,
    intro,
    footerNote,
    logo,
    children
}: OrgAuthShellProps): React.ReactElement {
    return (
        <div className="min-h-dvh bg-surface-ground flex flex-col items-center justify-center px-4 py-16">
            <div className="w-full max-w-[420px] flex flex-col gap-8 bg-surface-raised p-8">
                <header className="flex flex-col items-start text-left">
                    {logo ?? (
                        <img
                            src={LogoFull}
                            alt="Solomon Labs"
                            className="h-6 w-auto max-w-full shrink-0"
                        />
                    )}
                    {title && (
                        <Text
                            variant="h2"
                            className="mt-5 text-foreground-base text-pretty"
                        >
                            {title}
                        </Text>
                    )}
                    {intro && (
                        <Text
                            variant="body"
                            className={
                                title
                                    ? "mt-0.5 text-foreground-imprint text-pretty"
                                    : "mt-3 text-foreground-imprint text-pretty"
                            }
                        >
                            {intro}
                        </Text>
                    )}
                </header>

                <div className="flex flex-col gap-3">{children}</div>

                {footerNote && (
                    <Text
                        variant="fine-print"
                        className="text-center text-foreground-imprint text-pretty -mt-4"
                    >
                        {footerNote}
                    </Text>
                )}
            </div>
        </div>
    );
}
